import { useCallback, useEffect, useState } from "react";
import { Gift } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const MS_LABEL = { day1: "$50 · first day", day10: "$50 · day 10", day30: "$50 · day 30" };
const STATUS_LABEL = {
  submitted: "Received — we'll reach out to them",
  applied: "They applied",
  hired: "Hired",
  closed: "Closed",
};

export default function ReferralCard() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(null);
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", note: "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const load = useCallback(() => {
    api.get("/referrals/mine").then((r) => setMine(r.data.referrals || [])).catch(() => setMine([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/referrals", f);
      toast.success("Referral received — thank you!");
      setF({ first_name: "", last_name: "", phone: "", email: "", note: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="referral-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-heading text-sm font-semibold text-stone-900">
            <Gift size={18} className="text-primary" /> Refer a caregiver — earn up to $150
          </p>
          <p className="mt-0.5 text-xs text-stone-500">
            $50 after their first day worked, another $50 at day 10, and another at day 30. Tell us who they are and we'll take it from there.
          </p>
        </div>
        <Button variant="outline" onClick={() => setOpen((o) => !o)} className="rounded-full border-stone-300" data-testid="referral-toggle">
          {open ? "Close" : "Refer someone"}
        </Button>
      </div>
      {open && (
        <div className="mt-4 space-y-3 border-t border-stone-100 pt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Their first name *</Label><Input value={f.first_name} onChange={set("first_name")} className="border-stone-300" data-testid="referral-first" /></div>
            <div><Label className="text-xs">Their last name *</Label><Input value={f.last_name} onChange={set("last_name")} className="border-stone-300" data-testid="referral-last" /></div>
            <div><Label className="text-xs">Their phone *</Label><Input value={f.phone} onChange={set("phone")} placeholder="(000) 000-0000" className="border-stone-300" data-testid="referral-phone" /></div>
            <div><Label className="text-xs">Their email (or phone above)</Label><Input value={f.email} onChange={set("email")} className="border-stone-300" data-testid="referral-email" /></div>
          </div>
          <div><Label className="text-xs">Anything we should know (optional)</Label><Input value={f.note} onChange={set("note")} placeholder="e.g. has HHA experience, lives near Allentown" className="border-stone-300" data-testid="referral-note" /></div>
          <p className="text-[11px] text-stone-400">Tip: tell them to expect our call from a 954 number, and to mention your name when they apply.</p>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-primary text-primary-foreground" data-testid="referral-submit">
            {busy ? "Sending…" : "Send referral"}
          </Button>
        </div>
      )}
      {mine && mine.length > 0 && (
        <div className="mt-4 divide-y divide-stone-100 border-t border-stone-100" data-testid="my-referrals-list">
          {mine.map((r) => (
            <div key={r.id} className="py-2.5" data-testid={`my-referral-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-stone-800">{r.first_name} {r.last_name}</p>
                <p className="text-xs font-medium text-stone-500">{STATUS_LABEL[r.status] || r.status}</p>
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {Object.entries(MS_LABEL).map(([k, label]) => {
                  const m = (r.milestones || {})[k] || {};
                  const paid = m.status === "paid";
                  return (
                    <span key={k} className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${paid ? "bg-[#2F6B4F]/10 text-[#2F6B4F]" : "bg-stone-100 text-stone-500"}`}
                      data-testid={`my-referral-${r.id}-${k}`}>
                      {paid ? "✓ " : ""}{label}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
